'use client';

import { useCallback } from 'react';
import { useStore } from '@nanostores/react';

import {
  ButtonTool,
  TextFieldTool,
  useAgentTweakable,
  useAgentVisible,
} from '@/features/copilot/agent-tweakable';
import { $patientPanelSelectedTab } from '@/shared/stores';

import { selectPatientPanelTab } from '../lib';
import type { PatientWorkspaceTab } from '../model';

interface TabAgentBindingProps {
  tabId: string;
  patientUserId: string;
}

function TabAgentBinding({ tabId, patientUserId }: TabAgentBindingProps) {
  const onClick = useCallback(
    (clicked: boolean) => {
      if (!clicked) {
        return null;
      }
      selectPatientPanelTab(tabId, { actor: 'copilot', patientUserId });
      return { activeTab: tabId };
    },
    [tabId, patientUserId]
  );

  useAgentTweakable(ButtonTool, `selectTab:${tabId}`, false, onClick);
  return null;
}

function ActiveTabSignal() {
  const selectedTab = useStore($patientPanelSelectedTab);
  useAgentVisible(TextFieldTool, 'currentWorkspaceTab', selectedTab ?? '');
  return null;
}

export interface CopilotWorkspaceTabBindingsProps {
  tabs: ReadonlyArray<Pick<PatientWorkspaceTab, 'id'>>;
  patientUserId: string;
}

/**
 * Headless wiring that exposes the workspace tab strip to the copilot:
 *   - reads back the active tab via `currentWorkspaceTab`
 *   - registers one `selectTab:<id>` button per tab so the agent can navigate
 *
 * Per-request tweakables (status/urgency/note) intentionally live inside
 * `RequestListCard` — they are only registered when the host card is
 * rendered, which keeps the agent's tool surface in sync with what the
 * clinician can currently see.
 */
export function CopilotWorkspaceTabBindings({
  tabs,
  patientUserId,
}: CopilotWorkspaceTabBindingsProps) {
  return (
    <>
      <ActiveTabSignal />
      {tabs.map((tab) => (
        <TabAgentBinding key={tab.id} tabId={tab.id} patientUserId={patientUserId} />
      ))}
    </>
  );
}
